import { useEffect } from "react";
import { catchError, tap, throwError, type Observable } from "rxjs";

import { type APILibraryVerticalRequestType } from "@/core/data_sources/models/APILibraryVertical";
import { Store } from "@/core/data_sources/store";
import { AppStore } from "@/core/data_sources/store/app";
import { useService } from "@/core/utils/hooks";
import { requestToObservable } from "@/core/utils/observables";

import {
  deleteVertical,
  getVerticals,
  patchVertical,
  postVertical,
} from "../services";
import { LibraryVerticalsState } from "./state";

import type { LibraryVerticalsActions } from "./actions";
import type { LibraryVerticalsHooks } from "./hooks";

export class LibraryVerticalsStore extends Store<
  LibraryVerticalsState,
  LibraryVerticalsActions,
  LibraryVerticalsHooks
> {
  private static _instance: LibraryVerticalsStore;

  static get instance() {
    LibraryVerticalsStore._instance =
      LibraryVerticalsStore._instance || new LibraryVerticalsStore();
    return this._instance;
  }
  constructor() {
    super(
      new LibraryVerticalsState(),
      {
        createVertical: function (
          vertical: APILibraryVerticalRequestType,
        ): Observable<unknown> {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() => postVertical(vertical)).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
        updateVertical: function (
          id: number,
          vertical: Partial<APILibraryVerticalRequestType>,
        ): Observable<unknown> {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() =>
            patchVertical(Number(id), vertical),
          ).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
        deleteVertical: function (id: number): Observable<unknown> {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() => deleteVertical(Number(id))).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
      },
      {
        useVerticalStoreService: (q) => {
          const { data, error, loading, query, setQuery } = useService(
            getVerticals,
            q,
          );
          useEffect(() => {
            if (error) {
              return;
            }
            if (!loading && data) {
              this.state.setVerticals(data?.data ?? []);
            }
          }, [loading, data, error]);
          return { data, error, loading, query, setQuery };
        },
      },
    );
  }
}
